import Head from 'next/head'
import React, { useContext, useEffect } from 'react'
import styles from '../styles/OurApps.module.scss'
import Button from '../components/Common/Button/Button'
import AppContext from '../context/AppContext'
import Modal from '../components/Common/Modal/Modal'
import ContactForm from '../components/Common/Contact/Contact'
import homeStyles from '../styles/Home.module.scss'
/* ─── Data ─── */
const TICKER_ITEMS = [
  { color: '#2563EB', label: 'B2B Composable Commerce' },
  { color: '#2563EB', label: 'B2C Composable Commerce' },
  { color: '#2563EB', label: 'Lead Management' },
  { color: '#2563EB', label: 'AI Copilots' },
  { color: '#2563EB', label: 'Business Intelligence' },
]

const STATS = [
  { v: '5', l: 'Product Lines' },
  { v: '50+', l: 'Integrations' },
  { v: '30+', l: 'Industries' },
  { v: '99.9%', l: 'Uptime SLA' },
]

const PRODUCTS = [
  {
    id: 'b2b',
    number: '01',
    tag: 'B2B Solution',
    title: 'B2B Composable Commerce',
    subtitle: 'Enterprise-Grade. Infinitely Flexible.',
    description:
      'A headless, API-first commerce architecture designed for complex B2B workflows. Manage multi-buyer accounts, custom pricing tiers, contract-based purchasing, and complex order flows — all through a unified, modular platform that plugs into your existing tech stack.',
    features: [
      { icon: '⚡', label: 'Multi-Buyer Account Management' },
      { icon: '🔒', label: 'Contract & Quote-Based Ordering' },
      { icon: '💱', label: 'Custom Pricing & Volume Discounts' },
      { icon: '🔗', label: 'ERP / CRM Deep Integration' },
      { icon: '🌐', label: 'Multi-Catalog & Multi-Channel' },
      { icon: '📊', label: 'Approval Workflows & PO Management' },
    ],
    industries: [
      { icon: '🏭', name: 'Manufacturing' },
      { icon: '🛒', name: 'Wholesale & Distribution' },
      { icon: '💊', name: 'Pharma & Healthcare' },
      { icon: '🔧', name: 'Industrial Supply' },
      { icon: '🏗️', name: 'Construction' },
      { icon: '🚢', name: 'Logistics' },
    ],
    color: '#2563EB',
    bg: '#EFF6FF',
    reversed: false,
  },
  {
    id: 'b2c',
    number: '02',
    tag: 'B2C / D2C Solution',
    title: 'B2C Composable Commerce',
    subtitle: 'Sell Everywhere. Convert Always.',
    description:
      'A composable storefront engine built for speed, personalization, and conversion. Deliver pixel-perfect shopping experiences across web, mobile, and social channels with real-time inventory, AI-powered recommendations, and lightning-fast checkout flows.',
    features: [
      { icon: '🚀', label: 'Headless Storefront & PWA' },
      { icon: '🧠', label: 'AI-Powered Personalization' },
      { icon: '📱', label: 'Omnichannel & Social Commerce' },
      { icon: '⚡', label: '1-Click Checkout & Payments' },
      { icon: '📦', label: 'Real-Time Inventory Sync' },
      { icon: '🎯', label: 'Dynamic Promotions Engine' },
    ],
    industries: [
      { icon: '👗', name: 'Fashion & Apparel' },
      { icon: '💄', name: 'Beauty & Personal Care' },
      { icon: '🥗', name: 'Food & Beverage' },
      { icon: '🏋️', name: 'Sports & Fitness' },
      { icon: '🏠', name: 'Home & Lifestyle' },
      { icon: '📱', name: 'Electronics' },
    ],
    color: '#2563EB',
    bg: '#ECFDF5',
    reversed: true,
  },
  {
    id: 'retail',
    number: '03',
    tag: 'Retail CRM',
    title: 'Lead Management for Retail Stores',
    subtitle: 'Turn Footfall Into Revenue.',
    description:
      'A purpose-built CRM and lead intelligence platform for physical and phygital retail. Capture walk-in leads, track customer journeys across touchpoints, automate follow-ups, and empower your sales staff with real-time customer insights — all from a single dashboard.',
    features: [
      { icon: '📍', label: 'Walk-In Lead Capture & QR Flows' },
      { icon: '🔄', label: 'Automated Follow-Up Sequences' },
      { icon: '📲', label: 'WhatsApp & SMS Integration' },
      { icon: '👤', label: '360° Customer Profiles' },
      { icon: '📈', label: 'Sales Staff Performance Tracking' },
      { icon: '🗓️', label: 'Appointment & Demo Scheduling' },
    ],
    industries: [
      { icon: '🏢', name: 'Real Estate Showrooms' },
      { icon: '🚗', name: 'Auto & EV Dealerships' },
      { icon: '💎', name: 'Luxury & Jewelry' },
      { icon: '🛋️', name: 'Furniture & Interior' },
      { icon: '🖥️', name: 'Consumer Electronics' },
      { icon: '🏦', name: 'Banking & Insurance' },
    ],
    color: '#2563EB',
    bg: '#EFF6FF',
    reversed: false,
  },
  {
    id: 'ai',
    number: '04',
    tag: 'AI-Powered',
    title: 'AI Copilots for Global Commerce',
    subtitle: 'Intelligence That Acts, Not Just Advises.',
    description:
      'Autonomous AI agents embedded across your commerce operations — from intelligent product discovery and dynamic pricing to customer support and supply chain signals. Our Copilots learn, reason, and execute so your teams focus on strategy, not repetition.',
    features: [
      { icon: '🤖', label: 'Autonomous Commerce Agents' },
      { icon: '💬', label: 'Conversational Commerce & Support' },
      { icon: '🏷️', label: 'AI-Driven Dynamic Pricing' },
      { icon: '🔍', label: 'Semantic Search & Discovery' },
      { icon: '📣', label: 'Marketing Content Generation' },
      { icon: '🧩', label: 'Human-AI Collaboration Workflows' },
    ],
    industries: [
      { icon: '🏪', name: 'Global Retail Chains' },
      { icon: '📦', name: 'eCommerce Marketplaces' },
      { icon: '✈️', name: 'Travel & Hospitality' },
      { icon: '🏥', name: 'Healthcare Commerce' },
      { icon: '📚', name: 'EdTech & Publishing' },
      { icon: '🏦', name: 'Fintech & BFSI' },
    ],
    color: '#2563EB',
    bg: '#F5F3FF',
    reversed: true,
  },
  {
    id: 'bi',
    number: '05',
    tag: 'Analytics & BI',
    title: 'Business Intelligence for Global Commerce',
    subtitle: 'See Everything. Decide Faster.',
    description:
      'A commerce-native BI platform that aggregates data across your entire stack — storefronts, ERP, CRM, logistics — and surfaces actionable insights through real-time dashboards, predictive analytics, and trend intelligence. Stop reacting. Start anticipating.',
    features: [
      { icon: '📊', label: 'Unified Commerce Data Lake' },
      { icon: '🔮', label: 'Predictive Sales & Demand Forecasting' },
      { icon: '🌍', label: 'Cross-Market Trend Intelligence' },
      { icon: '🛒', label: 'Customer Lifetime Value Analytics' },
      { icon: '⚙️', label: 'Custom KPI Dashboards & Reports' },
      { icon: '🔔', label: 'Real-Time Alerts & Anomaly Detection' },
    ],
    industries: [
      { icon: '🏪', name: 'Retail & CPG' },
      { icon: '🌾', name: 'Agri & Food Tech' },
      { icon: '💊', name: 'Pharma Supply Chain' },
      { icon: '🚀', name: 'D2C Brands' },
      { icon: '🏭', name: 'Manufacturing' },
      { icon: '🌐', name: 'Global Exporters' },
    ],
    color: '#2563EB',
    bg: '#EFF6FF',
    reversed: false,
  },
]

/* ─── Illustrations (inline SVGs) ─── */
const B2BIllustration = ({ color, bg }: { color: string; bg: string }) => (
  <svg viewBox="0 0 380 280" style={{ width: '100%', maxWidth: 360 }} fill="none">
    <rect x="130" y="90" width="120" height="70" rx="12" fill={bg} stroke={color} strokeWidth="2" />
    <text x="190" y="119" textAnchor="middle" fill={color} fontSize="10" fontFamily="monospace" fontWeight="700">SOCOMMERZ</text>
    <text x="190" y="133" textAnchor="middle" fill={color} fontSize="9" fontFamily="monospace" opacity="0.7">CORE PLATFORM</text>
    <rect x="153" y="145" width="74" height="4" rx="2" fill={color} fillOpacity="0.15" />
    <path d="M130 118 L80 90" stroke={color} strokeWidth="1.5" strokeDasharray="5 3" strokeOpacity="0.4" />
    <path d="M130 130 L70 160" stroke={color} strokeWidth="1.5" strokeDasharray="5 3" strokeOpacity="0.4" />
    <path d="M250 118 L305 90" stroke={color} strokeWidth="1.5" strokeDasharray="5 3" strokeOpacity="0.4" />
    <path d="M250 130 L310 160" stroke={color} strokeWidth="1.5" strokeDasharray="5 3" strokeOpacity="0.4" />
    <path d="M170 160 L145 210" stroke={color} strokeWidth="1.5" strokeDasharray="5 3" strokeOpacity="0.4" />
    <path d="M210 160 L235 210" stroke={color} strokeWidth="1.5" strokeDasharray="5 3" strokeOpacity="0.4" />
    {[
      { x: 30, y: 68, w: 92, label: 'ERP', sub: 'SAP / Oracle' },
      { x: 20, y: 140, w: 92, label: 'CRM', sub: 'Salesforce' },
      { x: 258, y: 68, w: 92, label: 'PIM', sub: 'Akeneo' },
      { x: 258, y: 140, w: 92, label: 'OMS', sub: 'Order Mgmt' },
      { x: 92, y: 206, w: 92, label: 'PAYMENTS', sub: 'Stripe / Razorpay' },
      { x: 192, y: 206, w: 100, label: 'ANALYTICS', sub: 'BI Dashboard' },
    ].map((n) => (
      <g key={n.label}>
        <rect x={n.x} y={n.y} width={n.w} height={48} rx="8" fill="white" stroke={color} strokeWidth="1.5" strokeOpacity="0.5" />
        <text x={n.x + n.w / 2} y={n.y + 20} textAnchor="middle" fill={color} fontSize="9" fontFamily="monospace" fontWeight="700">{n.label}</text>
        <text x={n.x + n.w / 2} y={n.y + 34} textAnchor="middle" fill="#6B7280" fontSize="8" fontFamily="monospace">{n.sub}</text>
      </g>
    ))}
    <circle r="4" fill={color}>
      <animateMotion dur="3s" repeatCount="indefinite" path="M190 90 L80 90 L70 160 L130 125" />
    </circle>
  </svg>
)

const B2CIllustration = ({ color, bg }: { color: string; bg: string }) => (
  <svg viewBox="0 0 380 280" style={{ width: '100%', maxWidth: 360 }} fill="none">
    <rect x="130" y="20" width="120" height="220" rx="18" fill="white" stroke={color} strokeWidth="2" />
    <rect x="138" y="34" width="104" height="196" rx="12" fill={bg} />
    <rect x="162" y="22" width="56" height="8" rx="4" fill={color} fillOpacity="0.2" />
    <rect x="146" y="44" width="88" height="22" rx="8" fill="white" stroke={color} strokeWidth="1" strokeOpacity="0.3" />
    <circle cx="157" cy="55" r="4" fill={color} fillOpacity="0.4" />
    <rect x="165" y="51" width="50" height="8" rx="3" fill={color} fillOpacity="0.15" />
    {[[146, 74], [198, 74], [146, 130], [198, 130]].map(([x, y], i) => (
      <g key={i}>
        <rect x={x} y={y} width={46} height={50} rx="8" fill="white" stroke={color} strokeWidth="1" strokeOpacity="0.3" />
        <rect x={x + 6} y={y + 8} width={34} height={24} rx="5" fill={color} fillOpacity="0.12" />
        <rect x={x + 6} y={y + 36} width={20} height={7} rx="3" fill={color} fillOpacity="0.2" />
        <rect x={x + 28} y={y + 36} width={12} height={7} rx="3" fill={color} fillOpacity="0.35" />
      </g>
    ))}
    <rect x="146" y="188" width="88" height="28" rx="10" fill={color} />
    <text x="190" y="207" textAnchor="middle" fill="white" fontSize="9" fontFamily="monospace" fontWeight="700">ADD TO CART ⚡</text>
    <rect x="16" y="80" width="100" height="40" rx="10" fill="white" stroke={color} strokeWidth="1.5" strokeOpacity="0.5" />
    <text x="44" y="100" fill="#0D0D0D" fontSize="8" fontFamily="monospace" fontWeight="700">AI Picks</text>
    <text x="44" y="112" fill="#6B7280" fontSize="7" fontFamily="monospace">Personalized for you</text>
    <rect x="264" y="110" width="100" height="40" rx="10" fill="white" stroke="#F59E0B" strokeWidth="1.5" />
    <text x="292" y="130" fill="#0D0D0D" fontSize="8" fontFamily="monospace" fontWeight="700">Flash Sale</text>
    <text x="292" y="142" fill="#F59E0B" fontSize="7" fontFamily="monospace">40% OFF — 2h left</text>
  </svg>
)

const RetailIllustration = ({ color, bg }: { color: string; bg: string }) => (
  <svg viewBox="0 0 380 280" style={{ width: '100%', maxWidth: 360 }} fill="none">
    <rect x="120" y="60" width="140" height="120" rx="12" fill={bg} stroke={color} strokeWidth="2" />
    <rect x="136" y="80" width="44" height="36" rx="8" fill="white" stroke={color} strokeWidth="1.5" strokeOpacity="0.5" />
    <rect x="200" y="80" width="44" height="36" rx="8" fill="white" stroke={color} strokeWidth="1.5" strokeOpacity="0.5" />
    <rect x="158" y="136" width="64" height="44" rx="8" fill="white" stroke={color} strokeWidth="1.5" />
    <text x="190" y="162" textAnchor="middle" fill={color} fontSize="9" fontFamily="monospace" fontWeight="700">STORE</text>
    <path d={`M110 60 L190 30 L270 60`} fill={color} fillOpacity="0.1" stroke={color} strokeWidth="1.5" strokeOpacity="0.5" />
    <circle cx="190" cy="44" r="6" fill={color} fillOpacity="0.4" />
    {[
      { y: 20, w: 80, label: 'Walk-In Lead', c: color },
      { y: 74, w: 68, label: 'Captured', c: '#2563EB' },
      { y: 128, w: 56, label: 'Nurtured', c: '#2563EB' },
      { y: 182, w: 44, label: 'Converted', c: '#2563EB' },
    ].map((s, i) => (
      <g key={s.label}>
        <rect x={16} y={s.y} width={s.w} height={44} rx="8" fill="white" stroke={s.c} strokeWidth="1.5" />
        <text x={16 + s.w / 2} y={s.y + 28} textAnchor="middle" fill={s.c} fontSize="8" fontFamily="monospace" fontWeight="700">{s.label}</text>
        {i < 3 && <line x1={16 + s.w / 2} y1={s.y + 44} x2={16 + s.w / 2} y2={s.y + 52} stroke={s.c} strokeWidth="1.5" strokeDasharray="3 2" />}
      </g>
    ))}
    <rect x="268" y="100" width="100" height="80" rx="10" fill="white" stroke={color} strokeWidth="1.5" />
    <text x="318" y="118" textAnchor="middle" fill={color} fontSize="8" fontFamily="monospace" fontWeight="700">PIPELINE</text>
    {[30, 50, 38, 62, 48, 70, 55].map((h, i) => (
      <rect key={i} x={278 + i * 12} y={172 - h * 0.5} width={8} height={h * 0.5} rx="2" fill={color} fillOpacity={0.3 + i * 0.08} />
    ))}
  </svg>
)

const AIIllustration = ({ color, bg }: { color: string; bg: string }) => (
  <svg viewBox="0 0 380 280" style={{ width: '100%', maxWidth: 360 }} fill="none">
    <circle cx="190" cy="140" r="48" fill={bg} stroke={color} strokeWidth="2" />
    <circle cx="190" cy="140" r="32" fill="white" stroke={color} strokeWidth="1.5" strokeOpacity="0.5" />
    <text x="190" y="135" textAnchor="middle" fontSize="22">🧠</text>
    <text x="190" y="153" textAnchor="middle" fill={color} fontSize="8" fontFamily="monospace" fontWeight="700">AI COPILOT</text>
    <circle cx="190" cy="140" r="58" stroke={color} strokeWidth="1" strokeOpacity="0.2" fill="none">
      <animate attributeName="r" values="48;70;48" dur="3s" repeatCount="indefinite" />
      <animate attributeName="strokeOpacity" values="0.25;0;0.25" dur="3s" repeatCount="indefinite" />
    </circle>
    {[
      { cx: 60, cy: 50, label: 'Pricing' },
      { cx: 190, cy: 22, label: 'Discovery' },
      { cx: 320, cy: 50, label: 'Support' },
      { cx: 350, cy: 160, label: 'Marketing' },
      { cx: 280, cy: 248, label: 'Supply Chain' },
      { cx: 100, cy: 248, label: 'Analytics' },
      { cx: 30, cy: 160, label: 'Workflow' },
    ].map((n) => (
      <g key={n.label}>
        <line x1={n.cx} y1={n.cy} x2="190" y2="140" stroke={color} strokeWidth="1" strokeDasharray="4 3" strokeOpacity="0.3" />
        <circle cx={n.cx} cy={n.cy} r="24" fill="white" stroke={color} strokeWidth="1.5" />
        <text x={n.cx} y={n.cy + 5} textAnchor="middle" fill={color} fontSize="7" fontFamily="monospace" fontWeight="600">{n.label}</text>
      </g>
    ))}
  </svg>
)

const BIIllustration = ({ color, bg }: { color: string; bg: string }) => (
  <svg viewBox="0 0 380 280" style={{ width: '100%', maxWidth: 360 }} fill="none">
    <rect x="20" y="20" width="340" height="240" rx="14" fill="white" stroke={color} strokeWidth="2" />
    <rect x="20" y="20" width="340" height="36" rx="14" fill={color} />
    <rect x="20" y="44" width="340" height="12" fill={color} />
    <text x="38" y="44" fill="white" fontSize="10" fontFamily="monospace" fontWeight="700">SOCOMMERZ INTELLIGENCE DASHBOARD</text>
    {[
      { label: 'Revenue', val: '$2.4M', chg: '+18%', c: '#2563EB' },
      { label: 'Orders', val: '14,820', chg: '+32%', c: '#2563EB' },
      { label: 'Avg. CLV', val: '$340', chg: '+9%', c: color },
    ].map((k, i) => (
      <g key={k.label}>
        <rect x={30 + i * 112} y="68" width="104" height="54" rx="8" fill={bg} stroke={k.c} strokeWidth="1" strokeOpacity="0.5" />
        <text x={38 + i * 112} y="85" fill="#6B7280" fontSize="8" fontFamily="monospace">{k.label}</text>
        <text x={38 + i * 112} y="102" fill="#0D0D0D" fontSize="14" fontFamily="monospace" fontWeight="700">{k.val}</text>
        <text x={80 + i * 112} y="102" fill={k.c} fontSize="8" fontFamily="monospace" fontWeight="700">{k.chg} ↑</text>
      </g>
    ))}
    <rect x="30" y="132" width="210" height="112" rx="8" fill={bg} />
    <text x="40" y="148" fill="#6B7280" fontSize="8" fontFamily="monospace">SALES TREND — 8 WEEKS</text>
    <polyline points="44,224 68,208 96,196 120,178 148,188 172,162 200,150 228,142" stroke={color} strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <polyline points="44,224 68,208 96,196 120,178 148,188 172,162 200,150 228,142 228,230 44,230" fill={color} fillOpacity="0.08" />
    {[[44, 224], [68, 208], [96, 196], [120, 178], [148, 188], [172, 162], [200, 150], [228, 142]].map(([x, y], i) => (
      <circle key={i} cx={x} cy={y} r="3.5" fill="white" stroke={color} strokeWidth="2" />
    ))}
    <rect x="252" y="132" width="100" height="48" rx="8" fill="#EFF6FF" stroke={color} strokeWidth="1.5" />
    <text x="262" y="149" fill={color} fontSize="9" fontFamily="monospace" fontWeight="700">⚠ ANOMALY</text>
    <text x="262" y="163" fill="#6B7280" fontSize="7" fontFamily="monospace">Drop in SEA region</text>
    <text x="262" y="173" fill={color} fontSize="7" fontFamily="monospace">→ Action needed</text>
  </svg>
)

const ILLUSTRATIONS: Record<string, React.FC<{ color: string; bg: string }>> = {
  b2b: B2BIllustration,
  b2c: B2CIllustration,
  retail: RetailIllustration,
  ai: AIIllustration,
  bi: BIIllustration,
}

/* ─── Product Section Component ─── */
const ProductSection = ({
  id, number, tag, title, subtitle, description,
  features, industries, color, bg, reversed, index,
  onLearnMore,
}: (typeof PRODUCTS)[0] & { index: number; onLearnMore: () => void }) => {
  const Illust = ILLUSTRATIONS[id]

  return (
    <section
      id={id}
      className={styles.productSection}
      style={{ transitionDelay: `${index * 0.05}s` }}
    >
      {/* Watermark */}
      <span
        className={styles.productWatermark}
        style={{ left: reversed ? 'auto' : -10, right: reversed ? -10 : 'auto', color }}
      >
        {number}
      </span>

      {/* Accent strip */}
      <div
        className={styles.productAccent}
        style={{ background: `linear-gradient(90deg, ${color}, transparent)` }}
      />

      <div className={styles.productInner}>
        {/* Label row */}
        <div className={styles.productLabel}>
          <span className={styles.productLabelNum} style={{ color }}>{number}</span>
          <div className={styles.productLabelLine} />
          <span className={styles.chip} style={{ color, borderColor: color + '40', background: bg }}>
            {tag}
          </span>
        </div>

        <div className={`${styles.productGrid} ${reversed ? styles.productGridReversed : ''}`}>
          {/* Content */}
          <div>
            <h2 className={styles.productTitle}>
              <span style={{ color }}>{title.split(' ')[0]} </span>
              {title.split(' ').slice(1).join(' ')}
            </h2>
            <p className={styles.productSubtitle} style={{ color }}>— {subtitle}</p>
            <p className={styles.productDesc}>{description}</p>

            {/* Features */}
            <div className={styles.featuresGrid}>
              {features.map((f) => (
                <div
                  key={f.label}
                  className={styles.featurePill}
                  style={{ '--hover-color': color, '--hover-bg': bg } as React.CSSProperties}
                >
                  <span className={styles.featureIcon}>{f.icon}</span>
                  <span>{f.label}</span>
                </div>
              ))}
            </div>

            {/* Industries */}
            <div className={styles.industriesSection}>
              <p className={styles.industriesLabel}>Deployed Across Industries</p>
              <div className={styles.industriesTags}>
                {industries.map((ind) => (
                  <span key={ind.name} className={styles.indTag}>
                    <span>{ind.icon}</span>
                    <span>{ind.name}</span>
                  </span>
                ))}
              </div>
            </div>

            {/* CTAs */}
            <div className={styles.productCtas} onClick={onLearnMore}>
              <Button text='Learn More' classname='button-wrapper' />
            </div>
          </div>

          {/* Illustration */}
          <div className={styles.visualCardWrapper}>
            <div className={styles.visualCard}>
              <div className={styles.topbarDots}>
                <div className={styles.topbarDot} style={{ background: color }} />
                <div className={styles.topbarDot} style={{ background: '#e5e7eb' }} />
                <div className={styles.topbarDot} style={{ background: '#e5e7eb' }} />
                <span className={styles.topbarUrl}>socommerz.app / {id}</span>
              </div>
              <div className={styles.illustrationArea} style={{ background: bg }}>
                {Illust && <Illust color={color} bg={bg} />}
              </div>
              <div className={styles.bottomBar}>
                <span className={styles.bottomBarLabel}>socommerz · {tag}</span>
                <span className={styles.liveIndicator}>
                  <span className={styles.liveDot} style={{ background: color }} />
                  <span className={styles.liveText} style={{ color }}>Live</span>
                </span>
              </div>
            </div>
            <div className={styles.floatingBadge}>
              <div className={styles.badgeLabel}>Powered by</div>
              <div className={styles.badgeValue}>Socommerz</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ─── Main Page ─── */
const OurApps = () => {
  const { contactForm, setContactForm }: any = useContext(AppContext)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add(styles.visible)
        }),
      { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
    )
    document
      .querySelectorAll(`.${styles.productSection}, .${styles.ctaSection}`)
      .forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [])

  const doubled = [...TICKER_ITEMS, ...TICKER_ITEMS]

  const heroCards = [
    { n: '01', title: 'B2B Commerce', desc: 'Enterprise-grade composable architecture', color: '#2563EB', bg: '#EFF6FF', href: '#b2b' },
    { n: '02', title: 'B2C Commerce', desc: 'Headless storefronts for every channel', color: '#2563EB', bg: '#ECFDF5', href: '#b2b' },
    { n: '03', title: 'Lead Management', desc: 'Retail CRM to convert footfall', color: '#2563EB', bg: '#FFFBEB', href: '#retail' },
    { n: '04', title: 'AI Copilots', desc: 'Autonomous agents for commerce', color: '#2563EB', bg: '#F5F3FF', href: '#ai' },
    { n: '05', title: 'BI Platform', desc: 'Intelligence across global commerce', color: '#2563EB', bg: '#EFF6FF', href: '#bi', wide: true },
  ]

  return (
    <>
      <Head>
        <title>Our Apps | Socommerz — Composable Commerce Products</title>
        <meta name="title" content="Our Apps | Socommerz — Composable Commerce Products" />
        <meta
          name="description"
          content="Explore Socommerz's five composable commerce products: B2B Commerce, B2C Commerce, Lead Management, AI Copilots, and Business Intelligence. Enterprise-grade, modular, and built for global trade."
        />
        <meta
          name="keywords"
          content="socommerz products, B2B commerce, B2C commerce, retail CRM, AI copilots, business intelligence, composable commerce, headless commerce"
        />
        <meta name="robots" content="index,follow" />
        <link rel="canonical" href="https://www.socommerz.com/our-apps" />
        <link rel="alternate" href="https://www.socommerz.com/our-apps" hrefLang="en-in" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.socommerz.com/our-apps/" />
        <meta property="og:title" content="Our Apps | Socommerz — Composable Commerce Products" />
        <meta
          property="og:description"
          content="Explore Socommerz's five composable commerce products: B2B Commerce, B2C Commerce, Lead Management, AI Copilots, and Business Intelligence."
        />
        <meta property="twitter:url" content="https://www.socommerz.com/our-apps/" />
        <meta property="twitter:title" content="Our Apps | Socommerz — Composable Commerce Products" />
        <meta
          property="twitter:description"
          content="Explore Socommerz's five composable commerce products: B2B Commerce, B2C Commerce, Lead Management, AI Copilots, and Business Intelligence."
        />
      </Head>

      <main className={styles.ourAppsPage}>
        {/* ═══ Hero ═══ */}
        <section className={styles.hero}>
          <span className={styles.heroWatermark}>Apps</span>
          <div className={styles.heroStripe} />

          {/* Floating colour dots */}
          {TICKER_ITEMS.map((t, i) => (
            <div
              key={t.label}
              className={styles.heroFloatingDot}
              style={{
                background: t.color,
                top: `${18 + i * 14}%`,
                right: `${5 + (i % 3) * 4}%`,
                animationDelay: `${i * 0.8}s`,
                opacity: 0.7,
              }}
            />
          ))}

          <div className={styles.heroContent}>
            {/* Left */}
            <div className={styles.heroLeft}>
              <div className={styles.chip} style={{ color: '#2563EB', borderColor: '#BFDBFE', background: '#EFF6FF' }}>
                Our Apps
              </div>
              <h1 className={styles.heroTitle}>
                Composable<br />
                 <span className={homeStyles["span-gradient"]}>Commerce</span><br />
                <span style={{ color: '#2563EB' }}>Ecosystem</span>
              </h1>
              <p className={styles.heroDesc}>
                A composable commerce ecosystem built for the next era of global trade.
                Each product powerful alone — unstoppable together.
              </p>
              <div className={styles.heroBtns}>
                <a href="#b2b">
                  <Button text='Explore Products' classname='button-wrapper' />
                </a>
              </div>
            </div>

            {/* Right — Product Cards */}
            <div className={styles.heroCards}>
              {heroCards.map((card) => (
                <a
                  key={card.n}
                  href={card.href}
                  className={`${styles.heroCard} ${(card as any).wide ? styles.heroCardWide : ''}`}
                  style={{ background: card.bg, color: card.color }}
                >
                  <div className={styles.heroCardNum}>{card.n}</div>
                  <div className={styles.heroCardTitle}>{card.title}</div>
                  <div className={styles.heroCardDesc}>{card.desc}</div>
                </a>
              ))}
            </div>
          </div>

          {/* Stats Bar */}
          <div className={styles.statsBar}>
            <div className={styles.statsBarInner}>
              {STATS.map((s) => (
                <div key={s.l} className={styles.statItem}>
                  <span className={styles.statValue}>{s.v}</span>
                  <span className={styles.statLabel}>{s.l}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Ticker */}
          <div className={styles.ticker}>
            <div className={styles.tickerTrack}>
              {doubled.map((t, i) => (
                <span key={i} className={styles.tickerItem}>
                  <span className={styles.tickerDot} style={{ background: t.color }} />
                  {t.label}
                  <span style={{ color: '#e5e7eb', margin: '0 16px' }}>·</span>
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ═══ Divider ═══ */}
        <div className={styles.divider} />

        {/* ═══ Product Sections ═══ */}
        {PRODUCTS.map((p, i) => (
          <ProductSection key={p.id} {...p} index={i} onLearnMore={() => setContactForm(true)} />
        ))}
      </main>

      {contactForm && (
        <Modal>
          <ContactForm />
        </Modal>
      )}
    </>
  )
}

export default OurApps
